import { z } from "zod";

export * from "./execution";

/**
 * Control-flow socket constants
 * These are implicit sockets for execution ordering:
 * - Flow IN: Added to every non-trigger node (triggers are entry points with no predecessors)
 * - Flow OUT: Added to ALL nodes including triggers (to signal downstream execution)
 * Flow sockets carry no data, just completion signals with AND-join semantics.
 */
export const FLOW_IN_SOCKET_ID = "__flow_control_in__";
export const FLOW_OUT_SOCKET_ID = "__flow_control_out__";
export const FLOW_CONTROL_SOCKET_TYPE = "flow_control" as const;

/**
 * Sentinel value for conditional step outputs.
 *
 * When a conditional step (Decision, Confidence Threshold, etc.) does NOT take
 * a branch, the handler sets the untaken output socket to this value instead of
 * leaving it `undefined`. This distinguishes "branch not taken" (dead) from
 * "optional output with no data" (legitimate undefined).
 *
 * Dead-branch detection checks for this value — not `undefined` — so that
 * steps with legitimately optional outputs don't accidentally trigger
 * skip propagation.
 */
export const SOCKET_NOT_TAKEN = "__socket_not_taken__" as const;
export type SocketNotTaken = typeof SOCKET_NOT_TAKEN;

const socketNotTakenLiteral = z.literal(SOCKET_NOT_TAKEN);

/**
 * Wrap an output socket schema to also accept SOCKET_NOT_TAKEN.
 * Use this for mutually exclusive conditional outputs (Decision, Confidence Threshold, etc.)
 * so that the untaken branch can be explicitly marked as dead.
 */
export function conditionalSocket<T extends z.ZodTypeAny>(schema: T) {
  return z.union([schema, socketNotTakenLiteral]);
}

/**
 * Check if a socket ID is a flow socket
 */
export function isFlowSocket(socketId: string): boolean {
  return socketId === FLOW_IN_SOCKET_ID || socketId === FLOW_OUT_SOCKET_ID;
}

/**
 * Source-side cheap check: an output value is "potentially empty" if it is the
 * explicit not-taken sentinel or an empty array. Empty array becomes a real
 * dead branch only when the target socket is single-value (fanout-zero); the
 * full per-edge decision needs target socket info, but this predicate is the
 * shared cheap pre-check used wherever empty propagation kicks in.
 */
export function isPotentiallyEmptyOutput(value: unknown): boolean {
  return (
    value === SOCKET_NOT_TAKEN || (Array.isArray(value) && value.length === 0)
  );
}

/**
 * Check if a socket type indicates it expects an array.
 * Array socket types start with "array<" (e.g., "array<string>", "array<entity_reference:TYPE>")
 */
export function isArraySocketType(socketType: string | undefined): boolean {
  if (!socketType) return false;
  return socketType.startsWith("array<");
}

/**
 * Base socket types - defines what can flow between nodes
 */
export const BaseSocketTypes = [
  "string",
  "number",
  "boolean",
  "date",
  "json",
  "uuid",
  "entity_reference",
  "array<string>",
  "array<number>",
  "array<boolean>",
  "array<uuid>",
  "array<json>",
  "array<entity_reference>",
  "any",
  FLOW_CONTROL_SOCKET_TYPE, // Control-flow socket type for execution ordering
] as const;

/**
 * Socket type schema - allows base types and typed entity references
 * Examples: "string", "entity_reference", "entity_reference:AP_INVOICE", "array<entity_reference:CORE_DOCUMENT>"
 */
export const SocketTypeSchema = z.union([
  z.enum(BaseSocketTypes),
  // Typed entity reference (e.g., "entity_reference:AP_INVOICE")
  z.string().regex(/^entity_reference:[A-Z_]+$/),
  // Typed array of entity references (e.g., "array<entity_reference:CORE_DOCUMENT>")
  z.string().regex(/^array<entity_reference:[A-Z_]+>$/),
]);

export type SocketType = z.infer<typeof SocketTypeSchema>;

/**
 * Socket definition
 */
export const SocketDefinitionSchema = z.object({
  id: z.string(),
  name: z.string(),
  type: SocketTypeSchema,
  required: z.boolean().prefault(true),
  /** JSON often sends `null` for optional strings; coerce before `z.string()`. */
  description: z.preprocess(
    (v) => (v === null ? undefined : v),
    z.string().optional(),
  ) as unknown as z.ZodOptional<z.ZodString>,
  expression: z.preprocess(
    (v) => (v === null ? undefined : v),
    z.string().optional(),
  ) as unknown as z.ZodOptional<z.ZodString>,
});

/** `z.infer` widens this field to `string | undefined` as a required key; keep it optional in TS. */
type InferredSocketDefinition = z.infer<typeof SocketDefinitionSchema>;
export type SocketDefinition = Omit<
  InferredSocketDefinition,
  "description" | "expression"
> & {
  description?: string;
  expression?: string;
};

/**
 * Step category for UI organization
 */
export const StepCategorySchema = z.enum([
  "Data Access",
  "Data Transform",
  "DataSource Adapter",
  "DataSource Writer",
  "Transformation",
  "Integration",
  "Control Flow",
  "Utility",
  "Trigger",
  "AI & Agents",
  "Computer Use",
  "Debug",
]);

export type StepCategory = z.infer<typeof StepCategorySchema>;

/**
 * ERP module grouping for the workflow node library palette (palette-only;
 * does not affect execution or canvas category badges).
 */
export const StepLibraryModuleSchema = z.enum([
  "Platform",
  "AccountsPayable",
  "AccountsReceivable",
  "OrderManagement",
  "Procurement",
  "ShipAndDebit",
  "Rebates",
]);

export type StepLibraryModule = z.infer<typeof StepLibraryModuleSchema>;

/** ERP-only palette sections. */
export type ErpLibraryModule = Exclude<StepLibraryModule, "Platform">;

export const DEFAULT_LIBRARY_MODULE =
  "Platform" as const satisfies StepLibraryModule;

/**
 * Step definition
 */
export const StepDefinitionSchema = z.object({
  id: z.uuid(),
  type: z.string(),
  name: z.string(),
  category: StepCategorySchema,
  config: z.record(z.string(), z.unknown()),
  emits: z.array(z.string()).optional(),
  sockets: z.object({
    inputs: z.array(SocketDefinitionSchema),
    outputs: z.array(SocketDefinitionSchema),
  }),
  position: z.object({
    x: z.number(),
    y: z.number(),
  }),
});

type InferredStepDefinition = z.infer<typeof StepDefinitionSchema>;
export type StepDefinition = Omit<InferredStepDefinition, "sockets"> & {
  sockets: {
    inputs: SocketDefinition[];
    outputs: SocketDefinition[];
  };
};

/**
 * Edge definition
 */
export const EdgeDefinitionSchema = z.object({
  id: z.uuid(),
  sourceStepId: z.uuid(),
  sourceSocketId: z.string(),
  targetStepId: z.uuid(),
  targetSocketId: z.string(),
});

export type EdgeDefinition = z.infer<typeof EdgeDefinitionSchema>;

/**
 * Workflow run status
 */
export const WorkflowRunStatusSchema = z.enum([
  "queued",
  "running",
  "idle",
  "completed",
  "failed",
  "cancelled",
  "terminated",
  "review",
]);

export type WorkflowRunStatus = z.infer<typeof WorkflowRunStatusSchema>;

export const WORKFLOW_RUN_STATUS = {
  QUEUED: "queued",
  RUNNING: "running",
  IDLE: "idle",
  COMPLETED: "completed",
  FAILED: "failed",
  CANCELLED: "cancelled",
  TERMINATED: "terminated",
  REVIEW: "review",
} as const satisfies Record<string, WorkflowRunStatus>;

/**
 * Step run status
 */
export const StepRunStatusSchema = z.enum([
  "pending",
  "queued",
  "running",
  "completed",
  "failed",
  "skipped",
  "timed_out",
  "cancelled",
  "terminated",
  "review",
]);

export type StepRunStatus = z.infer<typeof StepRunStatusSchema>;

export const STEP_RUN_STATUS = {
  PENDING: "pending",
  QUEUED: "queued",
  RUNNING: "running",
  COMPLETED: "completed",
  FAILED: "failed",
  SKIPPED: "skipped",
  TIMED_OUT: "timed_out",
  CANCELLED: "cancelled",
  TERMINATED: "terminated",
  REVIEW: "review",
} as const satisfies Record<string, StepRunStatus>;

/**
 * Workflow retry configuration
 */
export const WorkflowRetryConfigSchema = z.object({
  enabled: z.boolean().prefault(false),
  maxRetries: z.number().prefault(0),
  initialDelay: z.number().prefault(1000),
  maxDelay: z.number().prefault(60000),
  backoffMultiplier: z.number().prefault(2),
});

export type WorkflowRetryConfig = z.infer<typeof WorkflowRetryConfigSchema>;
